import { Component, Input, OnInit, inject } from '@angular/core';
import { AbstractControl, UntypedFormGroup, Validators } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatDatepickerInputEvent, MatDatepickerIntl } from '@angular/material/datepicker';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { formatDateInput } from '@fcom/common/utils/form-utils';
import { LocalDate } from '@fcom/core';
import { LanguageService } from '@fcom/ui-translate';

import { CustomDateAdapter, dayMonthYearFormat } from '../../services/datepicker-adapter';
import { DatePickerLocalizationService } from '../../services/datepicker-localization.service';

export enum DatepickerTheme {
  DEFAULT = 'default',
  WEB_2 = 'web-2',
}

const DATE_INPUT_VALID_CHARS = /^[0-9.]*$/;

@Component({
  selector: 'fin-datepicker',
  styleUrls: ['./datepicker.component.scss'],
  templateUrl: 'datepicker.component.html',
  providers: [
    { provide: MatDatepickerIntl, useClass: DatePickerLocalizationService },
    { provide: DateAdapter, useClass: CustomDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: dayMonthYearFormat },
  ],
  standalone: false,
})
export class DatepickerComponent implements OnInit {
  private languageService = inject(LanguageService);
  private adapter = inject<DateAdapter<CustomDateAdapter>>(DateAdapter);

  @Input()
  parentForm: UntypedFormGroup;

  @Input()
  controlName: string;

  @Input()
  displayMargin = true;

  @Input()
  helpText?: string;

  @Input()
  translationPrefix: string;

  @Input()
  placeHolder: string = null;

  @Input()
  minDate: LocalDate = LocalDate.forDateObj(new Date(0));

  @Input()
  maxDate: LocalDate = LocalDate.now();

  @Input()
  id: string;

  @Input()
  icon: SvgLibraryIcon = SvgLibraryIcon.BOOK_FLIGHT;

  @Input()
  theme = DatepickerTheme.DEFAULT;

  @Input()
  startView: 'month' | 'year' | 'multi-year' = 'month';

  @Input()
  startAt: LocalDate;

  public readonly DatepickerTheme = DatepickerTheme;
  public readonly SvgLibraryIcon = SvgLibraryIcon;

  selectedDay: number;
  selectedMonth: number;
  selectedYear: number;
  isRequired = false;
  idOrControlName: string;

  ngOnInit(): void {
    this.setLocale();

    this.isRequired = this.required;
    this.idOrControlName = this.id || this.controlName;
  }

  setLocale(): void {
    const lang = this.languageService.localeValue.replace('_', '-');

    this.adapter.setLocale(lang);
  }

  signalSelection(eventValue: MatDatepickerInputEvent<unknown>): void {
    if (eventValue.value && eventValue.value instanceof LocalDate) {
      this.selectedDay = eventValue.value.localDay;
      this.selectedMonth = eventValue.value.localMonth;
      this.selectedYear = eventValue.value.localYear;
    }
  }

  get required(): boolean {
    return this.ctrlField.hasValidator(Validators.required);
  }

  get ctrlField(): AbstractControl {
    return this.parentForm.get(this.controlName);
  }

  onDateInput(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (
      !(event instanceof InputEvent) ||
      event.inputType.includes('deleteContent') ||
      !DATE_INPUT_VALID_CHARS.test(input.value)
    ) {
      return;
    }

    input.value = formatDateInput(input.value);
  }
}
